import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Chip,
  Divider,
  Paper,
  Collapse,
  IconButton,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import { Box } from "@mui/system";
import { useState } from "react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import _ from "lodash";
import { apiConstants, get, postGeneric } from "../../common/constants";
import { isAdmin } from "../../helpers/ReduxHelpers";
import { FormProvider, RHFSelectField } from "../../common/hook-form";
import { onlyUnique } from "../../helpers/UtilityHelper";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { downloadRulesCsv } from "../../helpers/allocationRulesCsv";

import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CategoryIcon from "@mui/icons-material/Category";
import LabelIcon from "@mui/icons-material/Label";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

function RuleItem({ rule, index }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1.5,
        borderRadius: 2,
        borderColor: expanded ? "#90caf9" : "#e3e8ef",
        overflow: "hidden",
        transition: "border-color 0.2s",
      }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.5,
          cursor: "pointer",
          "&:hover": { backgroundColor: "#f8f9fa" },
        }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              backgroundColor: "#e3f2fd",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #bbdefb",
            }}>
            <Typography variant="body2" fontWeight={700} color="#1976d2">
              {index + 1}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              Rule No. {index + 1}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
              {rule?.conditions?.slice(0, 3).map((con, j) => (
                <Chip
                  key={j}
                  label={`${con.fact}: ${con.operator} ${con.value}`}
                  size="small"
                  sx={{
                    fontSize: "0.7rem",
                    height: 22,
                    backgroundColor: "#f0f4f8",
                    fontWeight: 500,
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              ))}
              {rule?.conditions?.length > 3 && (
                <Chip
                  label={`+${rule.conditions.length - 3} more`}
                  size="small"
                  sx={{
                    fontSize: "0.7rem",
                    height: 22,
                    backgroundColor: "#e8eaed",
                    fontWeight: 500,
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            icon={<LocalShippingIcon sx={{ fontSize: "14px !important" }} />}
            label={`${rule.result.values.filter((v) => v).length} couriers`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontWeight: 600, fontSize: "0.75rem" }}
          />
          <IconButton size="small">
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color="text.secondary"
                sx={{ mb: 1.5, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 0.5 }}>
                Conditions
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {rule?.conditions.map((con, j) => (
                  <Box
                    key={j}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      borderRadius: 1.5,
                      backgroundColor: "#f8f9fa",
                    }}>
                    <Chip
                      label={con.fact}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        height: 24,
                        backgroundColor: "#e3f2fd",
                        color: "#1565c0",
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {con.operator}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {String(con.value).length > 60
                        ? String(con.value).substring(0, 60) + "..."
                        : con.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color="text.secondary"
                sx={{ mb: 1.5, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 0.5 }}>
                Courier Partners (Priority Order)
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {rule.result.values.map((cou, k) => (
                  <Box
                    key={k}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1.5,
                      backgroundColor: k === 0 ? "#e8f5e9" : "#f8f9fa",
                      border: k === 0 ? "1px solid #c8e6c9" : "none",
                    }}>
                    <Box
                      sx={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: k === 0 ? "#2e7d32" : "#9e9e9e",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                      }}>
                      {k + 1}
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={k === 0 ? 600 : 400}
                      sx={{ wordBreak: "break-all" }}>
                      {cou || "-"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
}

function Rules(props) {
  const data = props.data;
  const [allocVisible, setAllocVisible] = useState(false);
  const [rules, setRules] = useState([]);
  const [zoneName, setZoneName] = useState("");
  const [operator, setOperator] = useState("");
  const [weight, setWeight] = useState([]);
  const [delivered, setDelivered] = useState([]);
  const [del, setDel] = useState("");
  const methods = useForm();
  const Region = ["LOCAL", "REGIONAL", "METROS", "ROI", "SPECIAL"];
  const {
    handleSubmit,
    // reset,
    // setValue,
    // formState: { isSubmitting }
  } = methods;

  // "Show Allocation page on Merchant Panel" is a Proship-admin-only control (the
  // updatefield endpoint is @IsProshipAdmin) and only applies in the merchant-detail
  // context where `id` is present. Rules is also reused on screens that don't pass id.
  const showAllocationToggle = Boolean(props.id) && isAdmin();

  useEffect(() => {
    if (!showAllocationToggle) return;
    get(`${apiConstants.MERCHANT_DETAILS}/${props.id}`)
      .then((res) => setAllocVisible(res.data?.allocationVisibleMerchantPanel || false))
      .catch((err) => console.error(err));
  }, [showAllocationToggle, props.id]);

  const handleAllocVisible = async (event) => {
    const value = event.target.checked;
    setAllocVisible(value);
    try {
      await postGeneric(`${apiConstants.MERCHANT_DETAILS}/${props.id}/updatefield`, {
        field: "allocationVisibleMerchantPanel",
        value,
      });
    } catch (error) {
      setAllocVisible(!value); // revert optimistic update on failure
      console.error(error);
    }
  };

  let newData = data?.mappedOrderRule?.rules?.map((item) => {
    let zone = item?.conditions?.find((d) => {
      return d.fact === "ZONE";
    }) ?? { value: "" };
    let drop_pincode = item?.conditions?.find((d) => {
      return d.fact === "DROP_PINCODE";
    }) ?? { value: "" };
    let pickup_pincode = item?.conditions?.find((d) => {
      return d.fact === "PICKUP_PINCODE";
    }) ?? { value: "" };
    let weightKey = "";
    let weight = item?.conditions.filter((t) => t.fact === "WEIGHT") ?? { value: "" };
    if (weight?.length === 2) {
      weightKey =
        weight[0]?.operator +
        weight[0]?.value +
        " and " +
        weight[1]?.operator +
        weight[1]?.value;
    } else {
      weightKey = weight[0]?.operator + weight[0]?.value;
    }

    let order_valueKey = "";
    let order_value = item?.conditions.filter((t) => t.fact === "ORDER_VALUE") ?? { value: "" };
    if (order_value?.length === 2) {
      order_valueKey =
        order_value[0]?.operator +
        order_value[0]?.value +
        " and " +
        order_value[1]?.operator +
        order_value[1]?.value;
    } else {
      order_valueKey = order_value[0]?.operator + order_value[0]?.value;
    }


    let channelNameValue = item?.conditions.filter((t) => t.fact === "CHANNEL_NAME") ?? [];
    let channelName = channelNameValue[0] != null
      ? (channelNameValue[0]?.operator ?? "") + " " + (channelNameValue[0]?.value ?? "")
      : "";

    let oda_value = item?.conditions?.filter((d) => d.fact === "ODA_VALUE") ?? [];
    let oda_valueKey = oda_value[0] != null
      ? (oda_value[0]?.operator ?? "") + " " + (oda_value[0]?.value ?? "")
      : "";

    let payment_mode = item?.conditions?.find((d) => {
      return d.fact === "PAYMENT_MODE";
    }) ?? { value: "" };

    let shipment_type = item?.conditions?.find((d) => {
      return d.fact === "SHIPMENT_TYPE";
    }) ?? { value: "" };
    let dispatch_mode = item?.conditions?.find((d) => {
      return d.fact === "DISPATCH_MODE";
    }) ?? { value: "" };
    let unique_sku_count = item?.conditions?.find((d) => {
      return d.fact === "UNIQUE_SKU_COUNT";
    });

    let order_creation_time = item?.conditions?.find((d) => {
      return d.fact === "ORDER_CREATION_TIME";
    });

    let item_units = item?.conditions?.find((d) => {
      return d.fact === "ITEM_UNITS";
    });


    let rvp_reason = item?.conditions?.find((d) => {
      return d.fact === "RVP_REASON";
    }) ?? { value: "" };

    let qc_type = item?.conditions?.find((d) => {
      return d.fact === "QC_TYPE";
    }) ?? { value: "" };

    let abd_flag = item?.conditions?.find((d) => {
      return d.fact === "ABD_FLAG";
    }) ?? { value: "" };


    let DELIVERY = item?.conditions?.find((d) => {
      return d.fact === "DELIVERY_TYPE";
    }) ?? { value: "" };
    return {
      ...item,
      zone: zone?.value,
      drop_pincode: drop_pincode?.value,
      pickup_pincode: pickup_pincode?.value,
      weightKey,
      DELIVERY: DELIVERY?.value,
      order_valueKey,
      payment_mode: payment_mode?.value,
      rvp_reason: rvp_reason?.value,
      qc_type: qc_type?.value,
      abd_flag: abd_flag?.value,
      channelName,
      shipment_type: shipment_type?.value,
      dispatch_mode: dispatch_mode?.value,
      unique_sku_count: unique_sku_count?.value,
      order_creation_time: order_creation_time?.value,
      item_units: item_units?.value,
      // Split the combined weight/invoice bounds back into the upload template's
      // Min/Max columns so a downloaded file round-trips into the rule upload.
      minWeight: (weight?.find?.((w) => ['>=', '>'].includes(w?.operator)) || weight?.[0])?.value ?? '',
      maxWeight: (weight?.find?.((w) => ['<=', '<'].includes(w?.operator)) || weight?.[1])?.value ?? '',
      minInvoice: (order_value?.find?.((o) => ['>=', '>'].includes(o?.operator)) || order_value?.[0])?.value ?? '',
      maxInvoice: (order_value?.find?.((o) => ['<=', '<'].includes(o?.operator)) || order_value?.[1])?.value ?? '',
      oda_valueKey,
    };
  });


  useEffect(() => {
    let arr = [];
    let arr2 = [];
    newData?.map((item) => {
      if (item?.DELIVERY !== undefined) {
        arr2.push(item.DELIVERY);
      }
      if (item?.weightKey !== undefined) {
        arr.push(item?.weightKey);
      }
      return 0;
    });
    let char = arr?.filter(onlyUnique);
    const options = char?.map((op) => ({ label: op, value: op }));
    setWeight(options);
    let del = arr2.filter(onlyUnique);
    const options2 = del?.map((op) => ({ label: op, value: op }));
    setDelivered(options2);
  }, []);

  useEffect(() => {
    // let arr = [];
    let arr2 = [];
    newData?.map((item) => {
      if (zoneName !== "" && operator === "" && del === "") {
        if (item.zone === zoneName) {
          arr2.push(item);
        }
      }
      if (operator !== "" && zoneName === "" && del === "") {
        if (item.weightKey === operator) {
          // console.log(item)
          arr2.push(item);
        }
      }
      if (operator !== "" && zoneName !== "" && del === "") {
        if (item.weightKey === operator) {
          if (item.zone === zoneName) {
            arr2.push(item);
          }
        }
      }
      if (del !== "" && zoneName === "" && operator === "") {
        if (item.DELIVERY === del) {
          // console.log(item)
          arr2.push(item);
        }
      }
      if (operator !== "" && zoneName !== "" && del !== "") {
        if (item.weightKey === operator) {
          if (item.zone === zoneName) {
            if (item.DELIVERY === del) {
              arr2.push(item);
            }
          }
        }
      }
      if (operator !== "" && zoneName === "" && del !== "") {
        if (item.weightKey === operator) {
          if (item.DELIVERY === del) {
            arr2.push(item);
          }
        }
      }
      if (zoneName !== "" && operator === "" && del !== "") {
        if (item.zone === zoneName) {
          if (item.DELIVERY === del) {
            arr2.push(item);
          }
        }
      }
      return item;
    });
    setRules(arr2);
  }, [zoneName, operator, del]);

  function onSubmit() { }

  function zoneHandle(e) {
    setZoneName(e.target.value);
  }

  function operatorHandle(e) {
    setOperator(e.target.value);
  }

  function deliveryHandle(e) {
    setDel(e.target.value);
  }

  // Both this download and the rule-history snapshot download use the same shared
  // builder, so they produce byte-identical CSVs that match the ZONE_WEIGHT_RULE_UPLOAD
  // template (and CSV avoids Excel's 32,767-char cell limit on large pincode lists).
  const handleDownloadCsv = () => {
    downloadRulesCsv(newData, "Recommendation_Rules.csv");
  };

  return (
    <>
      <Card sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={700}>
              Recommendation Rules
            </Typography>
          }
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {showAllocationToggle && (
                <Tooltip title="When ON, the allocation (Shipping Rules) page is visible on the merchant panel">
                  <FormControlLabel
                    control={
                      <Switch checked={allocVisible} onChange={handleAllocVisible} />
                    }
                    label="Show Allocation page on Merchant Panel"
                    labelPlacement="start"
                    sx={{ mr: 1 }}
                  />
                </Tooltip>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleDownloadCsv}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Download CSV
              </Button>
            </Box>
          }
          sx={{ px: 3, pt: 3, pb: 1 }}
        />
        <CardContent sx={{ px: 3 }}>
          {data?.mappedOrderRule?.type === "CUSTOM" ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: 2,
                borderColor: "#e3e8ef",
                backgroundColor: "#fafbfc",
              }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <FilterListIcon sx={{ color: "#637381", fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                  Filter Rules
                </Typography>
              </Box>
              <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <RHFSelectField
                      name="ZoneName"
                      width="100%"
                      label="Zone Name"
                      value={zoneName}
                      options={Region.map((op) => ({
                        label: _.startCase(op),
                        value: op,
                      }))}
                      onChange={zoneHandle}
                    />
                  </Grid>
                  {weight?.length > 0 && (
                    <Grid item xs={12} md={3}>
                      <RHFSelectField
                        name="Weight"
                        width="100%"
                        label="Weight"
                        value={operator}
                        options={weight || []}
                        onChange={operatorHandle}
                      />
                    </Grid>
                  )}
                  {delivered?.length > 0 && (
                    <Grid item xs={12} md={3}>
                      <RHFSelectField
                        name="Delivery_Type"
                        width="100%"
                        label="Delivery type"
                        value={del}
                        options={delivered || []}
                        onChange={deliveryHandle}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={3}>
                    <Button
                      variant="outlined"
                      startIcon={<RestartAltIcon />}
                      size="small"
                      sx={{ textTransform: "none", borderRadius: 1.5 }}
                      onClick={() => {
                        setRules("");
                        setZoneName("");
                        setDel("");
                        setOperator("");
                      }}>
                      Reset Filters
                    </Button>
                  </Grid>
                </Grid>
              </FormProvider>
            </Paper>
          ) : (
            data?.mappedOrderRule?.type && (
              <Chip
                label={`Rule Type: ${data?.mappedOrderRule?.type}`}
                size="small"
                sx={{ mb: 2, fontWeight: 600 }}
              />
            )
          )}

          {rules?.length > 0 && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                {
                  label: "Category",
                  value: data?.mappedOrderRule?.category,
                  icon: <CategoryIcon sx={{ fontSize: 20 }} />,
                  color: "#e8f4fd",
                  iconColor: "#1976d2",
                },
                {
                  label: "Name",
                  value: data?.mappedOrderRule?.name,
                  icon: <LabelIcon sx={{ fontSize: 20 }} />,
                  color: "#f3e8fd",
                  iconColor: "#7b1fa2",
                },
                {
                  label: "Type",
                  value: data?.mappedOrderRule?.type,
                  icon: <InfoOutlinedIcon sx={{ fontSize: 20 }} />,
                  color: "#fff8e1",
                  iconColor: "#f57c00",
                },
                {
                  label: "Status",
                  value: data?.mappedOrderRule?.status,
                  icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
                  color: "#e8f5e9",
                  iconColor: "#2e7d32",
                },
              ].map((item) => (
                <Grid item xs={6} sm={3} key={item.label}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderColor: "#e3e8ef",
                      height: "100%",
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          backgroundColor: item.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: item.iconColor,
                        }}>
                        {item.icon}
                      </Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {item.label}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600} sx={{ pl: 0.5 }}>
                      {item.value || "-"}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}

          {rules?.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Showing
              </Typography>
              <Chip
                label={`${rules.length} rule${rules.length !== 1 ? "s" : ""}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          )}

          {rules?.length > 0 &&
            rules?.map((rule, i) => (
              <RuleItem key={i} rule={rule} index={i} />
            ))}
        </CardContent>
      </Card>
    </>
  );
}

export default Rules;
